/**
 * Ecommerce events that get sent to GA through GTM
 * @author juanmiguelrubio
 * @requires gtm.js
 *
 * The purchase object required for the methods in this module is in the following format:
 *
 * purchase: {
 *   email: 'lol@theinter.net',
 *   token: 'j7yJCqO-wJ4',
 *   total: 500,
 *   taxes: 40,
 *   markup: 25,
 *   insuranceAmount: 11.6,
 *   discountCode: 'BusSenda10', // optional
 *   international: false, // True if the purchase has an international trip
 *   purchaserNationality: 'México',
 *   passengerSelection: {
 *     adult: 1,
 *     child: 1, // optional
 *     infant: 1 // optional
 *   },
 *   phone: '+528181818181',
 *   serviceLevel: {
 *     name: 'Basic',
 *     amount: 20, // optional
 *     percent: null // optional
 *   },
 *   serviceLevelAmount: 20, // optional
 *   departs: {
 *     tripSlug: 'mont-gdlj-24mar170000-1093210-18',
 *     transporterName: 'Interjet',
 *     transportType: 'flight', // flight | bus
 *     allowsSeatSelection: true, // optional
 *     departure: '2017-03-16T00:20:00', // Anything that can be parsed with moment
 *     total: 200,
 *     commission: 10,
 *     originId: 't-ciudad-de-mexico-central-norte', // optional
 *     originName: 'Central del Norte', // optional
 *     originCityId: 'ciudad-de-mexico', // optional
 *     originCityName: 'Ciudad de México',
 *     originCountryName: 'México',
 *     destinationId: 't-ciudad-de-mexico-central-norte', // optional
 *     destinationName: 'Central de Houston', // optional
 *     destinationCityId: 'houston', // optional
 *     destinationCityName: 'Houston',
 *     destinationCountryName: 'Estados Unidos'
 *   },
 *   returns: { // optional
 *     // same attrs as departs
 *   }
 * }
 */

import moment from 'moment';
import dlPush from 'gtm';
import * as utils from 'ec.utils';

/**
 * Sends the Promotion Banners in the homepage to GTM
 *  promotion data:
 * [{
 *   id: 'banner-1',
 *   name: 'Mazatlán',
 *   creative: 'homeBanner',
 *   position: 'organic-1'
 * }, {
 *  ···
 * }]
 *
 * @param {Array} promotions List of displayed banners
 * @param {Function} callback [optional] A function to call after the data has been sent to GA
 */
export function trackPromotions(promotions, callback) {
  dlPush(
    {
      event: 'internalPromotions',
      ecommerce: {
        promoView: {
          promotions,
        },
      },
    },
    callback
  );
}

/**
 * Sends a promotionClick event to GTM
 *  banner data:
 * [{
 *   id: 'banner-1',
 *   name: 'Mazatlán',
 *   creative: 'homeBanner',
 *   position: 'organic-1'
 * }]
 *
 * @param {Array} banner Clicked promotion data
 * @param {Function} callback [optional] A function to call after the data has been sent to GA
 */
export function trackPromotionClick(banner, callback) {
  dlPush(
    {
      event: 'promotionClick',
      ecommerce: {
        promoClick: {
          promotions: banner,
        },
      },
    },
    callback
  );
}

/**
 * Sends impressions data to GTM
 *  result data:
 * [{
 *   id: 'mont-gdlj-24mar170000-1093210-18',
 *   originCityName: 'Houston',
 *   originCountryName: 'Estados Unidos',
 *   destinationCityName: 'Guadalajara',
 *   destinationCountryName: 'México',
 *   price: 150,
 *   commission: 10,
 *   lineName: 'ADO',
 *   transportType: 'Bus',
 *   departure: '2017-05-09', // Anything that can be parsed with moment
 *   position: 1
 * }, {
 *  ···
 * }]
 *
 * @param {String} listName The name of the list where the results are displayed
 * @param {Array} results List of displayed results
 * @param {Object} passengers [optional] Dictionary with passenger count by type
 * @param {String} way [optional] departure or return trip
 * @param {Function} callback [optional] A function to call after the data has been sent to GA
 */
export function trackProductList(
  listName,
  results,
  passengers,
  way = 'departure',
  callback
) {
  const impressions = results.map((result) => ({
    id: result.id,
    name: utils.ecTripProductName(result, way),
    price: result.price,
    commission: result.commission || 0,
    brand: result.lineName,
    category: utils.normalizeProperty(result.transportType),
    variant: moment(result.departure).format('YYYY-MM-DD'),
    list: result.listName,
    position: result.position,
  }));

  dlPush(
    {
      event: 'productList',
      passengersSelected: passengers
        ? utils.ecPassengerTypes(passengers)
        : 'NA',
      ecommerce: {
        currencyCode: 'MXN',
        impressions,
      },
    },
    callback
  );
}

/**
 * Sends a productClick event to GTM. Only trip products can be clicked
 *  trip data:
 * {
 *   id: 'mont-gdlj-24mar170000-1093210-18',
 *   originCityName: 'Houston',
 *   originCountryName: 'Estados Unidos',
 *   destinationCityName: 'Guadalajara',
 *   destinationCountryName: 'México',
 *   price: 150,
 *   commission: 10,
 *   lineName: 'ADO',
 *   transportType: 'Bus',
 *   departure: '2017-05-09', // Anything that can be parsed with moment
 *   position: 1
 * }
 *
 * @param {Object} trip A trip object as described above
 * @param {String} listName The name of the list where the results are displayed
 * @param {String} way [optional] departure or return trip
 * @param {Function} callback [optional] A function to call after the data has been sent to GA
 */
export function trackProductClick(trip, listName, way = 'departure', callback) {
  const { id, price, lineName, transportType, departure, position } = trip;

  dlPush(
    {
      event: 'productClick',
      ecommerce: {
        click: {
          actionField: { list: listName },
          products: [
            {
              id,
              name: utils.ecTripProductName(trip, way),
              price,
              brand: lineName,
              category: utils.normalizeProperty(transportType),
              variant: moment(departure).format('YYYY-MM-DD'),
              position,
            },
          ],
        },
      },
    },
    callback
  );
}

/**
 * Sends an addToCart event to GTM. There are three kinds of products that can be
 * added to the cart: 1) A single trip, 2) Insurance, 3) Markup. All this info
 * can be obtained from a purchase object received from the API
 *
 * @param {Object} purchase A purchase dictionary as described in the module comments
 * @param {Function} callback [optional] A function to call after the data has been sent to GA
 */
export function trackAddToCart(purchase, callback) {
  const {
    token,
    total,
    taxes,
    insuranceAmount,
    markup,
    discountCode = null,
    returns,
    serviceLevel,
    serviceLevelAmount,
  } = purchase;
  const products = utils.ecTripProducts(purchase);
  const departureMoment = moment(purchase.departs.departure).startOf('day');
  const departureDelta = moment
    .duration(departureMoment - moment().startOf('day'))
    .asDays();

  if (insuranceAmount) {
    products.push(utils.ecInsuranceProduct(purchase));
  }

  if (serviceLevel && serviceLevelAmount) {
    products.push(utils.ecServiceLevelProduct(purchase));
  }

  if (markup) {
    products.push(utils.ecMarkupProduct(purchase));
  }

  dlPush(
    {
      event: 'addToCart',
      departureDelta,
      ecommerce: {
        currencyCode: 'MXN',
        purchase: {
          actionField: {
            id: token,
            affiliation: returns ? 'Round' : 'Single',
            revenue: total,
            tax: taxes,
            shipping: 'NA',
            coupon: discountCode && 'NA',
          },
          products,
        },
        // TODO - Remove this when GA4 is completely implemented. Products are sent in the purchase object.
        add: {
          products,
        },
      },
    },
    callback
  );
}

/**
 * Sends a removeFromCart event to GTM. There are three kinds of products that can be
 * removed from the cart: 1) A single trip, 2) Insurance, 3) Markup. All this info
 * can be obtained from a purchase object received from the API
 *
 * @param {Object} purchase A purchase dictionary as described in the module comments
 * @param {Function} callback [optional] A function to call after the data has been sent to GA
 */
export function trackRemoveFromCart(purchase, callback) {
  const {
    insuranceAmount,
    markup,
    serviceLevel,
    serviceLevelAmount,
  } = purchase;
  const products = utils.ecTripProducts(purchase);

  if (insuranceAmount) {
    products.push(utils.ecInsuranceProduct(purchase));
  }

  if (serviceLevel && serviceLevelAmount) {
    products.push(utils.ecServiceLevelProduct(purchase));
  }

  if (markup) {
    products.push(utils.ecMarkupProduct(purchase));
  }

  dlPush(
    {
      event: 'removeFromCart',
      ecommerce: {
        currencyCode: 'MXN',
        add: {
          products,
        },
      },
    },
    callback
  );
}

/**
 * Sends a checkout event to GTM to track a purchase funnel.
 *
 * @param {Number} step Step in the funnel: 1 | 2 | 3 | 4
 * @param {Object} purchase A purchase dictionary as described in the module comments
 * @param {String} cardBrand Visa, mastercard, etc (Payment step only)
 * @param {Function} callback [optional] A function to call after the data has been sent to GA
 */
export function trackPurchaseFunnel(
  step,
  purchase,
  cardBrand = 'NA',
  callback
) {
  const { insuranceAmount, markup } = purchase;
  const products = utils.ecTripProducts(purchase);

  if (insuranceAmount) {
    products.push(utils.ecInsuranceProduct(purchase));
  }

  if (markup) {
    products.push(utils.ecMarkupProduct(purchase));
  }

  dlPush(
    {
      event: 'checkout',
      checkoutType: utils.funnelType(purchase),
      ecommerce: {
        checkout: {
          actionField: {
            step,
            option: cardBrand && 'NA',
          },
          products,
        },
      },
    },
    callback
  );
}

/**
 * Sends an ecommerce transaction to GTM.
 *
 * @param {Object} purchase A purchase dictionary as described in the module comments
 * @param {Function} callback [optional] A function to call after the data has been sent to GA
 */
export function trackPurchase(purchase, callback) {
  const {
    email,
    token,
    purchaserNationality,
    passengerSelection,
    phone,
    total,
    taxes,
    insuranceAmount,
    markup,
    discountCode = null,
    returns,
    serviceLevel,
    serviceLevelAmount,
    walletUsed,
    walletAmount,
    paymentType,
    isExchange,
    passengersQuantity,
    departs,
  } = purchase;
  const products = utils.ecTripProducts(purchase);

  if (insuranceAmount) {
    products.push(utils.ecInsuranceProduct(purchase));
  }

  if (serviceLevel && serviceLevelAmount) {
    products.push(utils.ecServiceLevelProduct(purchase));
  }

  if (markup) {
    products.push(utils.ecMarkupProduct(purchase));
  }

  dlPush(
    {
      event: 'purchase',
      email,
      phone,
      nationality: purchaserNationality,
      passengersSold: utils.ecPassengerTypes(passengerSelection),
      origin: departs ? departs.originCityName : 'NA',
      destination: departs ? departs.destinationCityName : 'NA',
      originTerminal: departs ? departs.originName : 'NA',
      destinationTerminal: departs ? departs.destinationName : 'NA',
      ecommerce: {
        purchase: {
          actionField: {
            id: token,
            affiliation: returns ? 'Round' : 'Single',
            revenue: total,
            tax: taxes,
            shipping: 'NA',
            coupon: discountCode && 'NA',
            walletUsed: walletUsed || 'NA',
            walletAmount: walletAmount || 0,
            paymentType: paymentType || 'NA',
            isExchange: isExchange || 'NA',
            passengersQuantity: passengersQuantity || 'NA',
          },
          products,
        },
      },
    },
    callback
  );
}



// WEBPACK FOOTER //
// ./ec.events.js